// Real-time duplicate detection alert - 95 lines ✅
// @requirement SPEC-IRO-001/FR-07: Real-time duplicate detection during recording
// @requirement SPEC-IRO-001/FR-08: Suggest similar resolved issues during recording

import { AlertTriangle, ExternalLink } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { SearchResult } from '@/types/knowledge'

interface DuplicateDetectionProps {
  duplicates: SearchResult[]
  similarResolved: SearchResult[]
  isDetecting?: boolean
  className?: string
}

export function DuplicateDetection({
  duplicates,
  similarResolved,
  isDetecting = false,
  className,
}: DuplicateDetectionProps): JSX.Element | null {
  const hasDuplicates = duplicates.length > 0
  const hasSimilar = similarResolved.length > 0

  if (!hasDuplicates && !hasSimilar && !isDetecting) {
    return null
  }

  return (
    <div className={cn('space-y-3', className)}>
      {isDetecting && (
        <Alert className="border-kaycoo-blue bg-kaycoo-blue-pale">
          <AlertDescription className="text-kaycoo-blue-dark">
            Checking for similar issues...
          </AlertDescription>
        </Alert>
      )}

      {hasDuplicates && (
        <Alert className="border-kaycoo-burgundy bg-kaycoo-burgundy-pale">
          <AlertTriangle className="h-4 w-4 text-kaycoo-burgundy-dark" />
          <AlertDescription>
            <p className="font-medium text-kaycoo-burgundy-dark mb-2">
              Potential duplicate issues found:
            </p>
            <ul className="space-y-2">
              {duplicates.map((dup) => (
                <li key={dup.issue.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <a
                      href={`/knowledge/${dup.issue.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-kaycoo-burgundy-dark hover:underline font-medium flex items-center gap-1"
                    >
                      {dup.issue.title}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <p className="text-sm text-kaycoo-charcoal-2 mt-1">
                      {Math.round(dup.similarity * 100)}% match
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {hasSimilar && (
        <Alert className="border-kaycoo-teal bg-kaycoo-teal-pale">
          <AlertDescription>
            <p className="font-medium text-kaycoo-teal-dark mb-2">
              Similar resolved issues that might help:
            </p>
            <ul className="space-y-3">
              {similarResolved.map((similar) => (
                <li key={similar.issue.id} className="space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <a
                      href={`/knowledge/${similar.issue.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-kaycoo-teal-dark hover:underline font-medium flex items-center gap-1 flex-1"
                    >
                      {similar.issue.title}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <Badge className="bg-kaycoo-teal text-white text-xs">
                      {Math.round(similar.similarity * 100)}% match
                    </Badge>
                  </div>
                  {similar.resolutionMethods.length > 0 && (
                    <p className="text-sm text-kaycoo-charcoal-2">
                      {similar.resolutionMethods.length} solution(s) available
                      {similar.issue.successRate && (
                        <span className="ml-2 text-kaycoo-teal-dark font-medium">
                          • {Math.round(similar.issue.successRate * 100)}% success rate
                        </span>
                      )}
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale"
              onClick={() => window.open('/knowledge', '_blank')}
            >
              Browse All Solutions
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
